import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { io as socketIoClient } from "socket.io-client";

const SocketContext = createContext({ socket: null, connected: false });

export function SocketProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const accountIdRef = useRef(null);
  const storeUser = useSelector((state) => state?.user?.user || null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:3004";

  useEffect(() => {
    const userDataRaw = localStorage.getItem("user");
    let localUser = userDataRaw ? JSON.parse(userDataRaw) : null;
    const sourceUser = storeUser || localUser;
    let accountIdValue = sourceUser?.accountId || sourceUser?._id || sourceUser?.userId || null;
    if (sourceUser && !sourceUser.accountId && accountIdValue && !storeUser) {
      const patched = { ...sourceUser, accountId: accountIdValue };
      localStorage.setItem("user", JSON.stringify(patched));
      localUser = patched;
    }
    const accountId = accountIdValue ? String(accountIdValue) : null;

    if (!accountId) {
      console.warn("[Socket] skipped init: missing accountId", { sourceUser: !!sourceUser });
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      accountIdRef.current = null;
      setConnected(false);
      return;
    }

    if (socketRef.current && accountIdRef.current === accountId) {
      return;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socket = socketIoClient(backendUrl, {
      transports: ["websocket"],
      auth: { accountId },
    });

    socketRef.current = socket;
    accountIdRef.current = accountId;

    const onConnect = () => { console.log("[Socket] connect", { accountId }); setConnected(true); };
    const onDisconnect = () => setConnected(false);
    const onUpsert = (payload) => {
      console.log("[Socket] app:upsert", payload);
    };
    const onOrderCreated = (payload) => {
      console.log("[Socket] orders:created", payload);
    };
    const onOrderFinalized = (payload) => {
      console.log("[Socket] orders:finalized", payload);
    };
    const onProductsUpdated = (payload) => {
      console.log("[Socket] products:updated", payload);
    };
    const onConnectError = (err) => {
      console.log("[Socket] connect_error", err?.message || err);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("app:upsert", onUpsert);
    socket.on("orders:created", onOrderCreated);
    socket.on("orders:finalized", onOrderFinalized);
    socket.on("products:updated", onProductsUpdated);
    socket.on("connect_error", onConnectError);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("app:upsert", onUpsert);
      socket.off("orders:created", onOrderCreated);
      socket.off("orders:finalized", onOrderFinalized);
      socket.off("products:updated", onProductsUpdated);
      socket.off("connect_error", onConnectError);
    };
  }, [backendUrl, storeUser]);

  const value = useMemo(() => ({ socket: socketRef.current, connected }), [connected]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket() {
  return useContext(SocketContext);
}
